/**
 * Playground v3 — combines v1 vibrancy (dual-axis, verdict badges, segmented bars)
 * with v2 full-page layout, plus new content: in-person panel interview, talent radar,
 * compensation, references, activity stream.
 */

/* ─── Stages ─── */

export const STAGES = [
  'applied', 'cv_screen', 'ai_interview', 'panel_interview', 'offer', 'hired',
] as const
export type Stage = typeof STAGES[number]

export const STAGE_LABELS: Record<Stage, string> = {
  applied: 'Applied',
  cv_screen: 'CV Screen',
  ai_interview: 'AI Interview',
  panel_interview: 'Panel Interview',
  offer: 'Offer',
  hired: 'Hired',
}

export const STAGE_COLORS: Record<Stage, string> = {
  applied:         'var(--text-tertiary)',
  cv_screen:       'var(--fg-info)',
  ai_interview:    '#8B5CF6',
  panel_interview: 'var(--accent)',
  offer:           'var(--fg-success)',
  hired:           'var(--fg-success)',
}

/* ─── Verdicts ─── */

export type VerdictTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral'
export interface Verdict {
  key: string
  label: string
  tone: VerdictTone
}

export const VERDICT_TONE_PALETTE: Record<VerdictTone, { fg: string; bg: string; ring: string }> = {
  success: { fg: 'var(--fg-success)', bg: 'var(--bg-success)', ring: 'var(--fg-success)' },
  info:    { fg: 'var(--fg-info)',    bg: 'var(--bg-info)',    ring: 'var(--fg-info)' },
  warning: { fg: 'var(--fg-warning)', bg: 'var(--bg-warning)', ring: 'var(--fg-warning)' },
  danger:  { fg: 'var(--fg-danger)',  bg: 'var(--bg-danger)',  ring: 'var(--fg-danger)' },
  neutral: { fg: 'var(--text-tertiary)', bg: 'var(--surface-tertiary)', ring: 'var(--border-emphasis)' },
}

export type HeadlineMatch = 'strong_match' | 'match' | 'needs_review' | 'not_a_fit'
export const HEADLINE_MATCH: Record<HeadlineMatch, Verdict> = {
  strong_match: { key: 'strong_match', label: 'Strong match',  tone: 'success' },
  match:        { key: 'match',        label: 'Match',         tone: 'info' },
  needs_review: { key: 'needs_review', label: 'Needs review',  tone: 'warning' },
  not_a_fit:    { key: 'not_a_fit',    label: 'Not a fit',     tone: 'danger' },
}

/* ─── Qualitative match ─── */

export type MatchLevel = 'very_strong' | 'strong' | 'moderate' | 'weak'
export const MATCH_LABELS: Record<MatchLevel, string> = {
  very_strong: 'Very strong',
  strong: 'Strong',
  moderate: 'Moderate',
  weak: 'Weak',
}
export const MATCH_PALETTE: Record<MatchLevel, { fg: string; bg: string }> = {
  very_strong: { fg: 'var(--fg-success)', bg: 'var(--bg-success)' },
  strong:      { fg: 'var(--fg-info)',    bg: 'var(--bg-info)' },
  moderate:    { fg: 'var(--fg-warning)', bg: 'var(--bg-warning)' },
  weak:        { fg: 'var(--fg-danger)',  bg: 'var(--bg-danger)' },
}

/* ─── People ─── */

export interface Author {
  id: string
  name: string
  initials: string
  color: string
  role?: string          // 'Hiring Manager', 'VP Finance', etc.
  title?: string
}

/* ─── Editable score primitives ─── */

export interface Override {
  id: string
  author: Author
  at: string
  newValue: boolean | number
  oldValue: boolean | number
  reason: string
}
export interface Comment {
  id: string
  author: Author
  at: string
  text: string
}

export interface Requirement {
  id: string
  label: string
  passed: boolean
  note?: string
  overrides?: Override[]
  comments?: Comment[]
}
export interface RankingCriterion {
  id: string
  label: string
  score: number             // 0..maxScore
  maxScore: number          // typically 10
  weight?: number           // 1..N — shown as ×weight badge
  note?: string
  overrides?: Override[]
  comments?: Comment[]
}

/* ─── Talent radar ─── */

export interface RadarDimension {
  id: string
  label: string
  score: number          // 0..10
  maxScore: number       // 10
  benchmark?: number     // market percentile for similar roles
  note?: string
}

/* ─── Compensation ─── */

export interface Compensation {
  currency: string
  currentTotal?: number       // currentTotal as a single composite
  askLow: number
  askHigh: number
  marketP50: number
  marketP75: number
  marketP90?: number
  bonusExpectation?: string
  equityExpectation?: string
  availableFrom?: string
  relocation?: 'yes' | 'no' | 'hybrid'
  notes?: string
}

/* ─── References ─── */

export interface Reference {
  id: string
  name: string
  initials: string
  color: string
  role: string
  company: string
  email?: string
  phone?: string
  vouched: boolean
  note?: string
}

/* ─── Stage reports ─── */

export interface CVScreeningReport {
  kind: 'cv_screen'
  headlineScore: number
  requirementsMet: number
  requirementsTotal: number
  processedAt: string
  requirements: Requirement[]
  criteria: RankingCriterion[]
  /** The AI's top-line "why this score" sentence */
  summary?: string
}

export interface AIInterviewReport {
  kind: 'ai_interview'
  headlineScore: number
  duration: string
  processedAt: string
  summary: string
  qualitativeFit: {
    communication: MatchLevel
    depth: MatchLevel
    problemSolving: MatchLevel
    culture: MatchLevel
  }
  competencies: RankingCriterion[]
  redFlags?: string[]
  greenFlags?: string[]
}

/* ─── In-person panel interview (NEW in v3) ─── */

export type PanelRecommendation = 'strong_yes' | 'yes' | 'lean_yes' | 'lean_no' | 'no' | 'strong_no'

export const PANEL_REC: Record<PanelRecommendation, { label: string; tone: VerdictTone }> = {
  strong_yes: { label: 'Strong yes', tone: 'success' },
  yes:        { label: 'Yes',        tone: 'success' },
  lean_yes:   { label: 'Lean yes',   tone: 'info' },
  lean_no:    { label: 'Lean no',    tone: 'warning' },
  no:         { label: 'No',         tone: 'danger' },
  strong_no:  { label: 'Strong no',  tone: 'danger' },
}

export interface PanelScorecard {
  id: string
  interviewer: Author
  panelRole: 'Hiring Manager' | 'Peer' | 'Cross-functional' | 'Skip-level' | 'Culture' | 'Technical' | string
  recommendation: PanelRecommendation
  confidence: 'low' | 'medium' | 'high'
  competencies: RankingCriterion[]    // 0–10 bars with notes
  narrative: string
  highlights?: string[]
  concerns?: string[]
  submittedAt: string
}

export interface PanelInterviewReport {
  kind: 'panel_interview'
  scheduledAt: string
  location: 'onsite' | 'video' | 'phone'
  durationMinutes: number
  overallScore: number            // 0..100 — roll-up
  overallRecommendation: PanelRecommendation
  decisionThreshold: number       // e.g. 75 — threshold to advance
  summary: string                 // AI roll-up of the panel
  scorecards: PanelScorecard[]
  consensusHighlights: string[]
  consensusConcerns: string[]
  nextSteps?: string[]
}

export interface TranscriptTurn {
  id: string
  speaker: 'interviewer' | 'candidate' | string
  speakerName?: string
  at: string
  text: string
  highlight?: boolean
}
export interface TranscriptReport {
  kind: 'transcript'
  source: 'ai_interview' | 'panel_interview'
  duration: string
  turns: TranscriptTurn[]
  keyMoments?: { at: string; label: string; note: string }[]
}

export type StageReport =
  | CVScreeningReport
  | AIInterviewReport
  | PanelInterviewReport
  | TranscriptReport

/* ─── Full-talent sections ─── */

export interface AIAssessment {
  headline: HeadlineMatch
  summary: string
  strengths: string[]
  growth: string[]
  /** NEW v3: a one-line hook for marketing-grade framing */
  hook?: string
}

export interface ICPCriterion {
  id: string
  label: string
  level: MatchLevel
  note?: string
}

export interface StageStep {
  stage: Stage
  state: 'done' | 'active' | 'upcoming'
  date?: string
  verdict?: Verdict
  score?: number        // headline score for that stage's report
}

export interface ExperienceEntry {
  id: string
  company: string
  role: string
  start: string
  end: string
  summary?: string
  highlights?: string[]
}

export interface ActivityEvent {
  id: string
  kind: 'stage_change' | 'message' | 'note' | 'system' | 'override' | 'panel' | 'email'
  text: string
  at: string
  by?: string
}

export interface Skill {
  label: string
  proficiency: MatchLevel
  yearsExperience?: number
}

export interface CandidateNote {
  id: string
  author: Author
  at: string
  text: string
}

export interface CandidateFile {
  id: string
  name: string
  kind: 'pdf' | 'doc' | 'image' | 'other'
  size: string
  uploadedAt: string
  thumbnail?: string
}

/* ─── The Candidate ─── */

export interface Candidate {
  id: string
  name: string
  initials: string
  role: string
  company: string
  location?: string
  relocationTo?: string
  email?: string
  phone?: string
  linkedinUrl?: string
  githubUrl?: string
  avatarColor: string

  stage: Stage
  status: 'for_review' | 'in_progress' | 'accepted' | 'rejected' | 'on_hold'
  daysInStage: number
  sources: string[]

  /* The headline number + verdict — big on the hero */
  overallScore: number          // 0..100
  overallConfidence: number     // 0..100 — AI confidence
  headlineMatch: HeadlineMatch

  /* Always-visible talent */
  ai: AIAssessment
  icp: ICPCriterion[]
  radar: RadarDimension[]
  stepper: StageStep[]
  experience: ExperienceEntry[]
  skills: Skill[]
  certifications?: string[]
  education?: { school: string; degree: string; year: string }[]
  compensation?: Compensation
  references?: Reference[]
  activity: ActivityEvent[]

  /* Stage reports */
  reports: StageReport[]

  /* Candidate-level */
  notes: CandidateNote[]
  files: CandidateFile[]
}

export interface Column {
  stage: Stage
  title: string
  candidates: Candidate[]
}
